import { useState, useEffect, useCallback, useMemo } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { membershipsAPI, paymentsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreditCard, Users, TrendingUp, Crown, Check, X, Loader2, ArrowRight, BadgePercent, Search, FileText, Upload, PauseCircle, PlayCircle, Repeat, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ImportDialog from '@/components/ImportDialog';
import api from '@/lib/api';

// TIERS no longer used in calculator — kept for assign dialog color reference

export default function MitgliedschaftenPage() {
  const navigate = useNavigate();
  const { user, isAdminPermissions, userPermissions } = useAuth();
  // Mitarbeiter-Sicht (Betreiber 22.09.2026): im Bereich Mitglieder nur
  // Mitgliederzahl, abgeschlossene Vertraege und Kuendigungen — keine
  // Umsatzzahlen, keine Preistabelle, keine Zahlungen, kein Import.
  const istInhaber = user?.role === 'admin' || !!isAdminPermissions;
  // Planwechsel (Betreiber 27.10.2026): auch der Vertrieb, wie der Server (verkauf.create_vertrag)
  const darfPlanwechsel = istInhaber || !!(userPermissions || {})['verkauf.create_vertrag'];
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [extras, setExtras] = useState({});
  const [memberships, setMemberships] = useState([]);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [hasStarterKit, setHasStarterKit] = useState(false);
  const [altPlaene, setAltPlaene] = useState([]);
  const [eigenerBeitrag, setEigenerBeitrag] = useState('');   // Altpreis, leer = Planpreis
  const [bemerkung, setBemerkung] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [tab, setTab] = useState(() => (user?.role === 'admin' || isAdminPermissions) ? 'plans' : 'active');
  const [vertraege, setVertraege] = useState([]);
  const [vertragSearch, setVertragSearch] = useState('');
  const [cancelledSearch, setCancelledSearch] = useState('');
  const [showImport, setShowImport] = useState(false);

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      if (!memberSearch) return true;
      const q = memberSearch.toLowerCase();
      return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
    }).slice(0, 50);
  }, [members, memberSearch]);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, memRes, statsRes, membersRes, txnRes, vtgRes] = await Promise.all([
        membershipsAPI.getPlans(),
        membershipsAPI.getAll(),
        membershipsAPI.getStats(),
        api.get('/leads', { params: { limit: 500 } }),
        paymentsAPI.getTransactions(),
        api.get('/vertraege'),
      ]);
      setPlans(plansRes.data.plans || []);
      setAltPlaene(plansRes.data.altplaene || []);
      setExtras(plansRes.data.extras || {});
      setMemberships(memRes.data.memberships || []);
      setStats(statsRes.data);
      setMembers(membersRes.data.leads || []);
      setTransactions(txnRes.data.transactions || []);
      setVertraege(vtgRes.data.vertraege || []);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error('Zahlungsstatus konnte nicht abgerufen werden'); return; }
    try {
      const res = await paymentsAPI.getStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        toast.success('Zahlung erfolgreich! Mitgliedschaft aktiviert.');
        setSearchParams({}); fetchData();
      } else if (res.data.stripe_status === 'expired') {
        toast.error('Zahlungssitzung abgelaufen'); setSearchParams({});
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
  };

  const handleAssign = async () => {
    if (!selectedMember || !assignDialog) return;
    setPaying(true);
    try {
      if (paymentMethod === 'stripe') {
        const res = await paymentsAPI.createCheckout({
          plan_id: assignDialog.id,
          member_id: selectedMember,
          origin_url: window.location.origin,
        });
        window.location.href = res.data.url;
        return;
      }
      const beitrag = eigenerBeitrag.trim() === '' ? null : Number(eigenerBeitrag.replace(',', '.'));
      if (beitrag !== null && (Number.isNaN(beitrag) || beitrag < 0)) { toast.error('Monatsbeitrag bitte als Zahl eingeben, z. B. 59,60'); setPaying(false); return; }
      await membershipsAPI.assign({
        member_id: selectedMember,
        plan_id: assignDialog.id,
        payment_method: 'manual',
        has_starter_kit: hasStarterKit,
        monatsbeitrag: beitrag,
        bemerkung: bemerkung.trim() || null,
      });
      toast.success('Mitgliedschaft zugewiesen!');
      setAssignDialog(null); setSelectedMember(''); setHasStarterKit(false); setEigenerBeitrag(''); setBemerkung(''); fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Zuweisen');
    } finally { setPaying(false); }
  };

  /* Kündigen und Planwechsel nur über den Vertrag (Betreiber 30.10.2026): Mit digitalem Vertrag führt der Knopf zum Vertrag
     (Kündigen-Dialog) bzw. zur Lead-Ansicht (Neue Laufzeit abschließen) — Vertrag und Mitgliedschaft bleiben zusammen.
     Ohne Vertrag (Altbestand) erklärt ein Dialog den Weg zum Bestandsvertrag; nur die Leitung darf die Mitgliedschaft allein
     ändern, und nur nach ausdrücklicher Rückfrage. */
  const [altbestand, setAltbestand] = useState(null);       // { m, art: 'kuendigen' | 'planwechsel' }
  const leitung = user?.role === 'admin' || user?.role === 'manager' || !!isAdminPermissions;
  const ueberVertrag = async (m, art) => {
    try {
      const r = (await membershipsAPI.vertrag(m.id)).data;
      if (r.vertrag_id) {
        if (art === 'kuendigen') navigate(`/vertraege?vertrag=${encodeURIComponent(r.vertrag_id)}&aktion=kuendigen`);
        else navigate(`/leads/${encodeURIComponent(m.member_id)}?laufzeit=1`);
        return;
      }
      setAltbestand({ m, art });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Der Vertrag konnte nicht geprüft werden'); }
  };
  const handleCancel = (m) => ueberVertrag(m, 'kuendigen');
  const altbestandKuendigen = async () => {
    if (!altbestand) return;
    setAktionBusy(true);
    try { await membershipsAPI.cancel(altbestand.m.id, { bestaetigt: true }); toast.success('Mitgliedschaft gekündigt'); setAltbestand(null); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehler beim Kündigen'); }
    finally { setAktionBusy(false); }
  };

  const handleMarkPaid = async (id) => {
    try { await membershipsAPI.markPaid(id); toast.success('Zahlung erfasst'); fetchData(); }
    catch { toast.error('Fehler beim Erfassen'); }
  };

  // Group plans by tier (memoized to avoid re-computation on each render)
  const adultPlans = useMemo(() => plans.filter(p => p.category === 'erwachsene'), [plans]);
  const kidPlans = useMemo(() => plans.filter(p => p.category === 'kinder'), [plans]);
  // Tab Aktive: nur laufende Mitgliedschaften (gekuendigte stehen unter Gekuendigt)
  // Pausierte bleiben in der Aktiven-Liste (mit Badge) — sie sind nicht gekündigt.
  // Sortieren (Betreiber 23.09.2026): nur fuer den Inhaber, in den Tabs Aktive und Gekuendigt.
  // Klick auf eine Spaltenueberschrift sortiert, zweiter Klick dreht die Richtung.
  // Auswahl „Sortieren“ (Betreiber 15.10.2026): in Aktive, Gekündigt und Verträge für alle mit Zugriff — neueste, älteste,
  // niedrigster und höchster Preis. Die Spaltenköpfe bleiben wie bisher nur für den Inhaber klickbar.
  const startSort = istInhaber ? { key: 'name', dir: 1 } : { key: null, dir: 1 };
  const [sortAktiv, setSortAktiv] = useState(startSort);
  const [sortGek, setSortGek] = useState(startSort);
  const [sortVertr, setSortVertr] = useState({ key: null, dir: 1 });
  const sortiere = (liste, sort, felder) => {
    const f = sort?.key && felder[sort.key]; if (!f) return liste;
    const leer = (w) => w === null || w === undefined || w === '' || (typeof w === 'number' && Number.isNaN(w));
    return [...liste].sort((x, y) => {
      const a = f(x), b = f(y);
      if (leer(a) || leer(b)) return leer(a) && leer(b) ? 0 : leer(a) ? 1 : -1;   // ohne Wert immer ans Ende
      if (typeof a === 'number' || typeof b === 'number') return (Number(a) - Number(b)) * sort.dir;
      return String(a).localeCompare(String(b), 'de', { sensitivity: 'base' }) * sort.dir;
    });
  };
  // Die vier Wahlen als Sortierzustand; `datum` ist das Feld, nach dem „neueste/älteste“ je Reiter geht
  const SORT_WAHL = { neueste: (datum) => ({ key: datum, dir: -1 }), aelteste: (datum) => ({ key: datum, dir: 1 }),
    preis_auf: () => ({ key: 'monatlich', dir: 1 }), preis_ab: () => ({ key: 'monatlich', dir: -1 }) };
  const wahlAus = (sort, datum) => (sort?.key === datum ? (sort.dir === -1 ? 'neueste' : 'aelteste')
    : sort?.key === 'monatlich' ? (sort.dir === 1 ? 'preis_auf' : 'preis_ab') : '');
  const SortWahl = ({ sort, setSort, datum, datumText, testid }) => (
    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <ArrowUpDown className="w-4 h-4" aria-hidden="true" />
      <select data-testid={testid} value={wahlAus(sort, datum)} aria-label="Sortieren"
        onChange={(e) => setSort(e.target.value ? SORT_WAHL[e.target.value](datum) : startSort)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
        <option value="">{istInhaber ? 'Name A–Z' : 'Standard'}</option>
        <option value="neueste">Neueste zuerst ({datumText})</option>
        <option value="aelteste">Älteste zuerst ({datumText})</option>
        <option value="preis_auf">Niedrigster Preis zuerst</option>
        <option value="preis_ab">Höchster Preis zuerst</option>
      </select>
    </label>
  );
  const SortKopf = ({ sort, setSort, k, children, className = '' }) => (
    <th className={`p-3 text-xs font-semibold text-muted-foreground ${className}`}>
      {istInhaber ? (
        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" data-testid={`sort-${k}`}
          onClick={() => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : 1 }))} title="Sortieren">
          {children}{sort.key === k ? <span className="text-[10px]">{sort.dir === 1 ? '▲' : '▼'}</span> : null}
        </button>
      ) : children}
    </th>
  );
  // Filtered vertraege by search
  const filteredVertraege = useMemo(() => {
    const q = vertragSearch.toLowerCase();
    const gefiltert = !vertragSearch ? vertraege : vertraege.filter(v =>
      v.full_name?.toLowerCase().includes(q) ||
      v.email?.toLowerCase().includes(q) ||
      v.telefon?.toLowerCase().includes(q) ||
      v.plan_name?.toLowerCase().includes(q) ||
      v.vertragsnummer?.toLowerCase().includes(q) ||
      v.ort?.toLowerCase().includes(q)
    );
    return sortiere(gefiltert, sortVertr, {
      beginn: v => v.vertragsbeginn || v.created_at, monatlich: v => (v.monthly_price === null || v.monthly_price === undefined ? null : Number(v.monthly_price)),
    });
  }, [vertraege, vertragSearch, sortVertr]);   // eslint-disable-line react-hooks/exhaustive-deps

  const aktiveMemberships = useMemo(() => sortiere(memberships.filter(m => m.status === 'active' || m.status === 'paused'), sortAktiv, {
    name: m => m.member_name, plan: m => m.plan_name, monatlich: m => Number(m.monthly_price || m.price || 0),
    beginn: m => m.start_date, ende: m => m.end_date, status: m => m.status,
  }), [memberships, sortAktiv]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Bestandsvertraege digital (23.09.2026): Fortschritt + Filter "nur ohne digitalen Vertrag"
  const [bestand, setBestand] = useState(null);
  const [nurOffen, setNurOffen] = useState(false);
  useEffect(() => { if (tab === 'active') membershipsAPI.bestandStand().then(r => setBestand(r.data)).catch(() => setBestand(null)); }, [tab, memberships]);
  const offenIds = useMemo(() => new Set((bestand?.liste || []).map(x => x.membership_id)), [bestand]);
  const papierVon = useMemo(() => Object.fromEntries((bestand?.liste || []).map(x => [x.membership_id, x.papier_original])), [bestand]);
  // Suche in „Aktive“ (29.09.2026): Name, Tarif, E-Mail, Telefon, Mitglieds- und Vertragsnummer.
  // E-Mail/Telefon/Nummern kommen aus Lead und Vertrag der Person, die Seite hat beide schon geladen.
  const [aktivSuche, setAktivSuche] = useState('');
  const suchText = useMemo(() => {
    const leadVon = Object.fromEntries(members.map(l => [l.id, l]));
    const vertragVon = {};
    for (const v of vertraege) { if (v.lead_id) (vertragVon[v.lead_id] = vertragVon[v.lead_id] || []).push(v); }
    const raus = {};
    for (const m of memberships) {
      const l = leadVon[m.member_id] || {};
      const vs = vertragVon[m.member_id] || [];
      raus[m.id] = {
        text: [m.member_name, m.plan_name, l.email, l.name, l.mitgliedsnummer, ...vs.flatMap(v => [v.email, v.vertragsnummer, v.mitgliedsnummer, v.full_name])]
          .filter(Boolean).join(' ').toLowerCase(),
        ziffern: [l.phone, ...vs.map(v => v.telefon)].filter(Boolean).join(' ').replace(/\D/g, ''),
      };
    }
    return raus;
  }, [memberships, members, vertraege]);
  const aktiveSichtbar = useMemo(() => {
    let liste = nurOffen ? aktiveMemberships.filter(m => offenIds.has(m.id)) : aktiveMemberships;
    const q = aktivSuche.trim().toLowerCase();
    if (!q) return liste;
    const woerter = q.split(/\s+/);
    const ziffern = q.replace(/\D/g, '');
    return liste.filter(m => {
      const s = suchText[m.id] || { text: (m.member_name || '').toLowerCase(), ziffern: '' };
      if (woerter.every(w => s.text.includes(w))) return true;
      return ziffern.length >= 4 && s.ziffern.includes(ziffern.replace(/^0/, ''));
    });
  }, [aktiveMemberships, nurOffen, offenIds, aktivSuche, suchText]);
  const [pauseFuer, setPauseFuer] = useState(null);      // membership
  const [pauseForm, setPauseForm] = useState({ von: '', bis: '', grund: '' });
  const [wechselFuer, setWechselFuer] = useState(null);  // membership
  const [wechselForm, setWechselForm] = useState({ plan_id: '', ab: '', monatsbeitrag: '', bemerkung: '' });
  const [aktionBusy, setAktionBusy] = useState(false);
  const pauseSpeichern = async () => {
    if (!pauseForm.von || !pauseForm.bis) { toast.error('Von und Bis angeben'); return; }
    setAktionBusy(true);
    try { const r = (await membershipsAPI.pause(pauseFuer.id, pauseForm)).data; toast.success(r.status === 'paused' ? 'Pausiert' : 'Pause vorgemerkt'); setPauseFuer(null); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Pause fehlgeschlagen'); }
    finally { setAktionBusy(false); }
  };
  const pauseAufheben = async (m) => {
    try { await membershipsAPI.pauseAufheben(m.id); toast.success('Pause beendet — wieder aktiv'); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };
  const wechselSpeichern = async () => {
    if (!wechselForm.plan_id) { toast.error('Neuen Plan wählen'); return; }
    setAktionBusy(true);
    try {
      await membershipsAPI.planwechsel(wechselFuer.id, { bestaetigt: true, plan_id: wechselForm.plan_id, ab: wechselForm.ab || null,
        monatsbeitrag: wechselForm.monatsbeitrag !== '' ? Number(wechselForm.monatsbeitrag) : null, bemerkung: wechselForm.bemerkung || null });
      toast.success('Plan gewechselt'); setWechselFuer(null); fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Planwechsel fehlgeschlagen'); }
    finally { setAktionBusy(false); }
  };

  // Cancelled memberships + vertraege combined
  const cancelledMembers = useMemo(() => {
    const cancelled = [];
    // From vertraege with status !== 'aktiv'
    vertraege.filter(v => ['gekuendigt', 'storniert'].includes(v.status)).forEach(v => {
      cancelled.push({
        id: v.id,
        type: 'vertrag',
        name: v.full_name,
        email: v.email,
        telefon: v.telefon,
        plan: v.plan_name,
        duration: v.duration_label,
        monthly_price: v.monthly_price,
        start_date: v.vertragsbeginn,
        cancelled_date: v.updated_at || v.created_at,
        vertragsnummer: v.vertragsnummer,
      });
    });
    // Nur wirklich gekuendigte (nicht superseded/paused); je Mitglied EINE Zeile:
    // der juengste Datensatz (Beginn, dann Anlage) — aeltere Import-Doppel entfallen
    const juengsteJeMitglied = new Map();
    memberships.filter(m => m.status === 'cancelled').forEach(m => {
      const alt = juengsteJeMitglied.get(m.member_id);
      const schluessel = `${m.start_date || ''}|${m.created_at || ''}`;
      if (!alt || schluessel > `${alt.start_date || ''}|${alt.created_at || ''}`) juengsteJeMitglied.set(m.member_id, m);
    });
    juengsteJeMitglied.forEach((m) => {
      // Avoid duplicates if vertrag already added
      if (!cancelled.find(c => c.name === m.member_name && c.plan === m.plan_name)) {
        cancelled.push({
          id: m.id,
          type: 'membership',
          name: m.member_name,
          email: '',
          telefon: '',
          plan: m.plan_name,
          duration: m.duration_label || `${m.duration_months || '?'} Mon`,
          monthly_price: m.monthly_price || m.price || 0,
          start_date: m.start_date,
          cancelled_date: m.cancelled_at || m.updated_at,
          vertragsnummer: '',
        });
      }
    });
    return cancelled;
  }, [vertraege, memberships]);

  const filteredCancelled = useMemo(() => {
    const q = cancelledSearch.toLowerCase();
    const gefiltert = !cancelledSearch ? cancelledMembers : cancelledMembers.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.telefon?.toLowerCase().includes(q) ||
      c.plan?.toLowerCase().includes(q) ||
      c.vertragsnummer?.toLowerCase().includes(q)
    );
    return sortiere(gefiltert, sortGek, {
      name: c => c.name, plan: c => c.plan, monatlich: c => Number(c.monthly_price || 0),
      beginn: c => c.start_date, gekuendigt: c => c.cancelled_date,
    });
  }, [cancelledMembers, cancelledSearch, sortGek]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sortedAdultPlans = useMemo(
    () => adultPlans.slice().sort((a, b) => a.sort_order - b.sort_order),
    [adultPlans]
  );

  if (loading) return (
    <div className="space-y-6 fade-in">
      <div className="h-8 w-48 bg-muted animate-pulse rounded" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <div key={`skeleton-${i}`} className="h-64 bg-muted animate-pulse rounded-lg" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="memberships-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Mitgliedschaften</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            Originalpläne aus dem P1 Mitgliedschaftsvertrag
          </p>
        </div>
        {istInhaber && (
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-members-btn" className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm">
            <Upload className="w-3.5 h-3.5" /> Mitglieder importieren
          </Button>
        )}
      </div>

      <ImportDialog open={showImport} onClose={() => setShowImport(false)} onSuccess={fetchData} />

      {/* Stats */}
      <div className={`grid grid-cols-2 gap-3 ${istInhaber ? 'lg:grid-cols-5' : 'lg:grid-cols-3'}`} data-testid="membership-stats">
        <Card className="border-border/60"><CardContent className="p-4">
          <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Aktive Mitglieder</p>
          <p className="text-2xl font-bold mt-1">{stats?.active || 0}</p>
        </CardContent></Card>
        <Card className="border-border/60"><CardContent className="p-4">
          <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Abgeschlossene Verträge</p>
          <p className="text-2xl font-bold mt-1">{vertraege.length}</p>
        </CardContent></Card>
        <Card className="border-border/60"><CardContent className="p-4">
          <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Gekündigt</p>
          <p className="text-2xl font-bold mt-1">{stats?.cancelled || 0}</p>
        </CardContent></Card>
        {istInhaber && (
          <Card className="border-border/60" data-testid="stat-umsatz"><CardContent className="p-4">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Monatl. Umsatz</p>
            <p className="text-2xl font-bold mt-1">{stats ? <UmsatzWert wert={stats.monthly_revenue} format={(v) => `${v.toFixed(0)}€`} /> : '…'}</p>
          </CardContent></Card>
        )}
        {istInhaber && (
          <Card className="border-border/60"><CardContent className="p-4">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Gesamt</p>
            <p className="text-2xl font-bold mt-1">{stats?.total || 0}</p>
          </CardContent></Card>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1 w-fit" data-testid="membership-tabs">
        {[
          { key: 'plans', label: 'Preistabelle', nurInhaber: true },
          { key: 'vertraege', label: `Verträge (${vertraege.length})` },
          { key: 'active', label: `Aktive (${aktiveMemberships.length})` },
          { key: 'cancelled', label: `Gekündigt (${cancelledMembers.length})` },
          { key: 'transactions', label: 'Zahlungen', nurInhaber: true },
        ].filter((t) => istInhaber || !t.nurInhaber).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              tab === t.key ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* PLANS TABLE TAB */}
      {tab === 'plans' && istInhaber && (
        <div className="space-y-6">
          {/* Adult Plans */}
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Erwachsene</h2>
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="adult-plans-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 font-semibold">Plan</th>
                      <th className="text-center px-3 py-3 font-semibold">Laufzeit</th>
                      <th className="text-right px-3 py-3 font-semibold">Wöchentlich</th>
                      <th className="text-right px-3 py-3 font-semibold">Monatlich</th>
                      <th className="text-center px-3 py-3 font-semibold">Ersparnis</th>
                      <th className="text-right px-4 py-3 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAdultPlans.map((p) => (
                      <tr key={p.id} className="border-b border-border/30 hover:bg-muted/20 transition-colors" data-testid={`plan-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-8 rounded-full" style={{ backgroundColor: p.color }} />
                            <div>
                              <div className="font-semibold">{p.name}</div>
                              <div className="text-[10px] text-muted-foreground">{p.subtitle}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge variant="outline" className="text-[10px]">{p.duration_label}</Badge>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-sm">
                          {p.weekly_price.toFixed(2).replace('.', ',')} &#8364;
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="font-bold text-base">{p.monthly_price.toFixed(2).replace('.', ',')} &#8364;</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {p.savings_vs_1m ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium bg-emerald-500/10 px-2 py-0.5 rounded-full">
                              <BadgePercent className="w-3 h-3" />
                              -{p.savings_vs_1m.per_month.toFixed(2).replace('.', ',')} &#8364;/Mon
                            </span>
                          ) : <span className="text-muted-foreground text-xs">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" className="h-7 text-xs" style={{ backgroundColor: p.color }}
                            onClick={() => { setAssignDialog(p); setSelectedMember(''); setPaymentMethod('manual'); setHasStarterKit(false); }}
                            data-testid={`assign-plan-${p.id}`}>
                            Zuweisen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Kids Plans */}
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Kinder &amp; Jugend</h2>
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="kids-plans-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 font-semibold">Plan</th>
                      <th className="text-center px-3 py-3 font-semibold">Laufzeit</th>
                      <th className="text-right px-3 py-3 font-semibold">Wöchentlich</th>
                      <th className="text-right px-3 py-3 font-semibold">Monatlich</th>
                      <th className="text-right px-4 py-3 font-semibold">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kidPlans.map((p) => (
                      <tr key={p.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`plan-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-8 rounded-full" style={{ backgroundColor: p.color }} />
                            <div>
                              <div className="font-semibold">{p.name}</div>
                              <div className="text-[10px] text-muted-foreground">{p.subtitle}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center"><Badge variant="outline" className="text-[10px]">{p.duration_label}</Badge></td>
                        <td className="px-3 py-3 text-right font-mono">{p.weekly_price.toFixed(2).replace('.', ',')} &#8364;</td>
                        <td className="px-3 py-3 text-right font-bold">{p.monthly_price.toFixed(2).replace('.', ',')} &#8364;</td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" className="h-7 text-xs" style={{ backgroundColor: p.color }}
                            onClick={() => { setAssignDialog(p); setSelectedMember(''); setPaymentMethod('manual'); setHasStarterKit(false); }}
                            data-testid={`assign-plan-${p.id}`}>
                            Zuweisen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Altverträge: Bestandskunden mit Altpreisen (Octiv/Lexware). Nicht im
              Verkauf, aber zuweisbar — der Monatsbeitrag lässt sich im Dialog anpassen. */}
          {altPlaene.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="altvertraege">
              <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Altverträge (Bestandskunden)</h3>
                  <p className="text-xs text-muted-foreground">Alte Tarife ohne Bindung. Richtpreis aus Lexware — beim Zuweisen kann der tatsächliche Altbeitrag eingetragen werden.</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border/50 text-xs text-muted-foreground">
                    <th className="text-left px-4 py-2 font-semibold">Alttarif</th>
                    <th className="text-right px-3 py-2 font-semibold">Richtpreis / Monat</th>
                    <th className="text-right px-4 py-2 font-semibold">Aktion</th>
                  </tr></thead>
                  <tbody>
                    {altPlaene.map((p) => (
                      <tr key={p.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`altplan-row-${p.id}`}>
                        <td className="px-4 py-2"><div className="font-medium">{p.name}</div><div className="text-[10px] text-muted-foreground">{p.subtitle}</div></td>
                        <td className="px-3 py-2 text-right font-mono">{(p.monthly_price ?? 0).toFixed(2).replace('.', ',')} &#8364;</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => { setAssignDialog(p); setSelectedMember(''); setPaymentMethod('manual'); setHasStarterKit(false); setEigenerBeitrag(''); setBemerkung(''); }}
                            data-testid={`assign-plan-${p.id}`}>
                            Zuweisen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Extras — heterogene Struktur: Betreuter Einstieg (regulaer/tag1),
              Check-Up (standard/intensiv), Vorauszahler (Prozente), Alt-Format (price) */}
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">TAG 1 ANGEBOT &amp; Extras</h3>
            <div className="flex flex-wrap gap-3">
              {Object.values(extras).map((e) => {
                const fmt = (v) => `${(v ?? 0).toFixed(2).replace('.', ',')} €`;
                let detail = '';
                if (typeof e.price === 'number') detail = `${fmt(e.price)}${e.type ? ` (${e.type})` : ''}`;
                else if (typeof e.regulaer === 'number') detail = `${fmt(e.regulaer)} regulär · ${fmt(e.tag1)} am Tag 1`;
                else if (e.standard?.preis != null) detail = `${e.standard.minuten} Min ${fmt(e.standard.preis)} · ${e.intensiv?.minuten} Min ${fmt(e.intensiv?.preis)}`;
                else if (typeof e.m12 === 'number') detail = `${Math.round(e.m12 * 100)} % (12 Mon.) · ${Math.round((e.m24 || 0) * 100)} % (24 Mon.)`;
                return (
                  <div key={e.name} className="bg-card border border-border rounded-lg px-4 py-2">
                    <div className="text-sm font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground">{detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}


      {/* VERTRAEGE TAB */}
      {tab === 'vertraege' && (
        <div className="space-y-4" data-testid="vertraege-section">
          {/* Search Bar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="vertrag-search"
                value={vertragSearch}
                onChange={e => setVertragSearch(e.target.value)}
                placeholder="Mitglied suchen (Name, E-Mail, Telefon, Plan, Nr.)..."
                className="pl-9"
              />
              {vertragSearch && (
                <button onClick={() => setVertragSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <SortWahl sort={sortVertr} setSort={setSortVertr} datum="beginn" datumText="Vertragsbeginn" testid="vertrag-sort" />
          </div>
          {vertragSearch && (
            <p className="text-xs text-muted-foreground">{filteredVertraege.length} von {vertraege.length} Verträgen</p>
          )}

          <Card className="border-border/60" data-testid="vertraege-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Nr.</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Name</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Plan</th>
                      <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Monatlich</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">IBAN</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Beginn</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Erstellt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVertraege.map(v => (
                      <tr key={v.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`vertrag-row-${v.id}`}>
                        <td className="p-3 font-mono text-xs">{v.vertragsnummer}</td>
                        <td className="p-3">
                          <div className="font-medium">{v.full_name}</div>
                          <div className="text-[10px] text-muted-foreground">{v.email}</div>
                          {v.telefon && <div className="text-[10px] text-muted-foreground">{v.telefon}</div>}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-[10px]">{v.plan_name} ({v.duration_label})</Badge>
                        </td>
                        <td className="p-3 text-right font-semibold">{v.monthly_price?.toFixed(2).replace('.', ',')} &#8364;</td>
                        <td className="p-3 font-mono text-xs">{v.iban ? v.iban.slice(0, 4) + '...' + v.iban.slice(-4) : '-'}</td>
                        <td className="p-3 text-xs">{v.vertragsbeginn ? new Date(v.vertragsbeginn).toLocaleDateString('de-DE') : '-'}</td>
                        <td className="p-3">
                          <Badge className={`text-[10px] ${v.status === 'aktiv' ? 'bg-[#10B981]' : 'bg-gray-500'}`}>
                            {v.status === 'aktiv' ? 'Aktiv' : 'Gekündigt'}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleDateString('de-DE') : '-'}</td>
                      </tr>
                    ))}
                    {filteredVertraege.length === 0 && (
                      <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">
                        {vertragSearch ? (
                          <div className="space-y-1">
                            <Search className="w-8 h-8 mx-auto opacity-30" />
                            <p>Kein Vertrag gefunden für „{vertragSearch}"</p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <FileText className="w-8 h-8 mx-auto opacity-30" />
                            <p>Noch keine Verträge vorhanden</p>
                          </div>
                        )}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ACTIVE MEMBERSHIPS TAB */}
      {tab === 'active' && bestand && (
        <Card className="border-border/60" data-testid="bestand-stand">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase">Verträge digital erfasst</p>
              <p className="text-2xl font-bold mt-1">{bestand.digital} <span className="text-sm font-normal text-muted-foreground">von {bestand.gesamt}</span></p>
              <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden"><div className="h-full bg-[#10B981]" style={{ width: `${bestand.gesamt ? Math.round(100 * bestand.digital / bestand.gesamt) : 0}%` }} /></div>
              <p className="text-xs text-muted-foreground mt-1">{bestand.offen} offen{bestand.verloren ? ` · ${bestand.verloren} mit verlorenem Papiervertrag zuerst` : ''}. Beim nächsten Besuch am Tablet bestätigen lassen: Lead öffnen → „Vertrag digital erfassen“.</p>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="accent-marke" checked={nurOffen} onChange={e => setNurOffen(e.target.checked)} data-testid="filter-nur-offen" /> nur ohne digitalen Vertrag</label>
          </CardContent>
        </Card>
      )}
      {tab === 'active' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="active-search" value={aktivSuche} onChange={e => setAktivSuche(e.target.value)}
                placeholder="Aktive Mitglieder suchen: Name, Tarif, E-Mail, Telefon, Mitglieds-/Vertragsnr." className="pl-9" autoComplete="off" />
              {aktivSuche && (
                <button onClick={() => setAktivSuche('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Suche leeren">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <SortWahl sort={sortAktiv} setSort={setSortAktiv} datum="beginn" datumText="Vertragsbeginn" testid="active-sort" />
          </div>
          {aktivSuche && <p className="text-xs text-muted-foreground">{aktiveSichtbar.length} von {aktiveMemberships.length} Ergebnissen</p>}
        </div>
      )}
      {tab === 'active' && (
        <Card className="border-border/60">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <SortKopf sort={sortAktiv} setSort={setSortAktiv} k="name" className="text-left">Mitglied</SortKopf>
                    <SortKopf sort={sortAktiv} setSort={setSortAktiv} k="plan" className="text-left">Plan</SortKopf>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Laufzeit</th>
                    <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Wöchentlich</th>
                    <SortKopf sort={sortAktiv} setSort={setSortAktiv} k="monatlich" className="text-right">Monatlich</SortKopf>
                    <SortKopf sort={sortAktiv} setSort={setSortAktiv} k="status" className="text-left">Status</SortKopf>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Zahlung</th>
                    <SortKopf sort={sortAktiv} setSort={setSortAktiv} k="ende" className="text-left">Gültig bis</SortKopf>
                    <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {aktiveSichtbar.map((m) => (
                    <tr key={m.id} className="border-b border-border/30 hover:bg-muted/30" data-testid={`membership-row-${m.id}`}>
                      <td className="p-3 font-medium">
                        {m.member_id ? (
                          <button type="button" className="hover:underline text-left" onClick={() => navigate(`/leads/${m.member_id}`)} data-testid={`membership-open-${m.id}`} title="Mitglied öffnen">{m.member_name}</button>
                        ) : m.member_name}
                        {bestand && offenIds.has(m.id) && m.member_id && (
                          <button type="button" onClick={() => navigate(`/leads/${m.member_id}?digital=1`)} title="Vertrag digital erfassen" data-testid={`digital-${m.id}`}
                            className={`ml-2 inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-normal hover:bg-muted ${papierVon[m.id] === 'verloren' ? 'border-red-500 text-red-600' : 'border-amber-500 text-amber-600'}`}>
                            {papierVon[m.id] === 'verloren' ? 'Papier verloren' : 'nur Papier'}
                          </button>
                        )}
                      </td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{m.plan_name}</Badge></td>
                      <td className="p-3 text-xs text-muted-foreground">{m.duration_label || `${m.duration_months || '?'} Mon`}</td>
                      <td className="p-3 text-right text-xs">{m.weekly_price ? `${m.weekly_price.toFixed(2).replace('.', ',')} \u20AC` : '-'}</td>
                      <td className="p-3 text-right font-semibold">
                        <button type="button" className="hover:underline" title={`Beitrag ändern${m.beitrag_bemerkung ? ` · ${m.beitrag_bemerkung}` : ''}`} data-testid={`beitrag-${m.id}`} onClick={async () => {
                          const eingabe = window.prompt(`Monatsbeitrag für ${m.member_name} (aktuell ${(m.monthly_price || 0).toFixed(2).replace('.', ',')} €):`, String(m.monthly_price ?? '').replace('.', ','));
                          if (eingabe === null) return;
                          const wert = Number(String(eingabe).replace(',', '.'));
                          if (Number.isNaN(wert) || wert < 0) { toast.error('Ungültiger Betrag'); return; }
                          const bemerkung = window.prompt('Bemerkung (optional):', m.beitrag_bemerkung || '') || '';
                          try { await membershipsAPI.beitrag(m.id, wert, bemerkung); toast.success('Beitrag geändert'); fetchData(); }
                          catch (e) { toast.error(e?.response?.data?.detail || 'Ändern fehlgeschlagen'); }
                        }}>{(m.monthly_price || m.price || 0).toFixed(2).replace('.', ',')} &#8364;{m.preis_individuell ? <span className="ml-1 text-[10px] text-muted-foreground font-normal">indiv.</span> : null}</button>
                      </td>
                      <td className="p-3">
                        <Badge className={`text-[10px] ${m.status === 'active' ? 'bg-[#10B981]' : m.status === 'paused' ? 'bg-amber-500' : 'bg-gray-500'}`}>
                          {m.status === 'active' ? 'Aktiv' : m.status === 'paused' ? 'Pausiert' : 'Gekündigt'}
                        </Badge>
                        {m.pause && <p className="text-[10px] text-muted-foreground mt-0.5">{m.status === 'paused' ? 'bis' : 'ab'} {new Date(m.status === 'paused' ? m.pause.bis : m.pause.von).toLocaleDateString('de-DE')}{m.pause.grund ? ` · ${m.pause.grund}` : ''}</p>}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-[10px] ${m.payment_status === 'paid' ? 'border-[#10B981] text-[#10B981]' : 'border-amber-500 text-amber-500'}`}>
                          {m.payment_status === 'paid' ? 'Bezahlt' : 'Ausstehend'}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground text-xs">{m.end_date ? new Date(m.end_date).toLocaleDateString('de-DE') : '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {m.payment_status !== 'paid' && m.status === 'active' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleMarkPaid(m.id)} data-testid={`mark-paid-${m.id}`}>Bezahlt</Button>
                          )}
                          {(m.status === 'active' || m.status === 'paused') && darfPlanwechsel && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs" title="Planwechsel" onClick={() => ueberVertrag(m, 'planwechsel')} data-testid={`wechsel-mem-${m.id}`}>
                              <Repeat className="w-3 h-3" />
                            </Button>
                          )}
                          {m.status === 'active' && istInhaber && !m.pause && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs" title="Pausieren" onClick={() => { setPauseFuer(m); setPauseForm({ von: new Date().toISOString().slice(0, 10), bis: '', grund: '' }); }} data-testid={`pause-mem-${m.id}`}>
                              <PauseCircle className="w-3 h-3" />
                            </Button>
                          )}
                          {m.pause && istInhaber && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600" title="Pause beenden" onClick={() => pauseAufheben(m)} data-testid={`pause-ende-${m.id}`}>
                              <PlayCircle className="w-3 h-3" />
                            </Button>
                          )}
                          {m.status === 'active' && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" title="Kündigen (über den Vertrag)" onClick={() => handleCancel(m)} data-testid={`cancel-mem-${m.id}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {aktiveSichtbar.length === 0 && (
                    <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">{aktivSuche.trim() ? `Keine Treffer für „${aktivSuche.trim()}“` : nurOffen ? 'Alle laufenden Verträge sind digital erfasst' : 'Keine aktiven Mitgliedschaften'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CANCELLED TAB */}
      {/* Pause */}
      <Dialog open={!!pauseFuer} onOpenChange={(o) => { if (!o) setPauseFuer(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Mitgliedschaft pausieren · {pauseFuer?.member_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-muted-foreground">Von</label><Input type="date" value={pauseForm.von} onChange={(e) => setPauseForm({ ...pauseForm, von: e.target.value })} data-testid="pause-von" /></div>
              <div><label className="text-xs text-muted-foreground">Bis (letzter Pausentag)</label><Input type="date" value={pauseForm.bis} onChange={(e) => setPauseForm({ ...pauseForm, bis: e.target.value })} data-testid="pause-bis" /></div>
            </div>
            <Input value={pauseForm.grund} onChange={(e) => setPauseForm({ ...pauseForm, grund: e.target.value })} placeholder="Grund (Verletzung, Urlaub, …)" data-testid="pause-grund" />
            <p className="text-xs text-muted-foreground">Kein Beitrag im Pausenzeitraum, das Vertragsende verschiebt sich um die Pausendauer. Die App zeigt „pausiert“. Start und Ende laufen automatisch.</p>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setPauseFuer(null)}>Abbrechen</Button><Button onClick={pauseSpeichern} disabled={aktionBusy} data-testid="pause-speichern">Pausieren</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Altbestand ohne digitalen Vertrag: Kündigen und Planwechsel gehen nur über den Vertrag (30.10.2026) */}
      <Dialog open={!!altbestand} onOpenChange={(o) => { if (!o) setAltbestand(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="altbestand-dialog">
          <DialogHeader><DialogTitle>{altbestand?.art === 'kuendigen' ? 'Kündigen' : 'Planwechsel'} · {altbestand?.m?.member_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Zu dieser Mitgliedschaft gibt es <b>keinen digitalen Vertrag</b> (Altbestand). {altbestand?.art === 'kuendigen' ? 'Gekündigt' : 'Gewechselt'} wird sonst über den Vertrag, damit Vertrag und Mitgliedschaft zusammenbleiben.</p>
            <p className="text-xs text-muted-foreground">Bitte zuerst den Bestandsvertrag erfassen (Lead-Ansicht › „Vertrag digital erfassen“ oder „Papiervertrag fotografieren“).</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAltbestand(null)}>Schließen</Button>
              <Button size="sm" onClick={() => { const id = altbestand.m.member_id; setAltbestand(null); navigate(`/leads/${encodeURIComponent(id)}`); }} data-testid="altbestand-zum-lead">Zum Bestandsvertrag</Button>
            </div>
            {leitung && (
              <div className="border-t border-border/60 pt-3 space-y-2" data-testid="altbestand-leitung">
                <p className="text-xs text-amber-700">Nur Leitung, nur Altbestand: die Mitgliedschaft allein {altbestand?.art === 'kuendigen' ? 'kündigen' : 'wechseln'} — ohne Vertrag, auf ausdrückliche Rückfrage.</p>
                {altbestand?.art === 'kuendigen' ? (
                  <Button size="sm" variant="outline" className="text-red-600" disabled={aktionBusy} onClick={altbestandKuendigen} data-testid="altbestand-kuendigen">Mitgliedschaft trotzdem kündigen</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => { const m = altbestand.m; setAltbestand(null); setWechselFuer(m); setWechselForm({ plan_id: '', ab: '', monatsbeitrag: '', bemerkung: '' }); }} data-testid="altbestand-wechseln">Plan trotzdem hier wechseln</Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Planwechsel (nur noch Altbestand ohne Vertrag, Leitung) */}
      <Dialog open={!!wechselFuer} onOpenChange={(o) => { if (!o) setWechselFuer(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Planwechsel · {wechselFuer?.member_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Aktuell: <b>{wechselFuer?.plan_name}</b> · {(wechselFuer?.monthly_price || 0).toFixed(2).replace('.', ',')} €/Monat</p>
            <Select value={wechselForm.plan_id} onValueChange={(v) => setWechselForm({ ...wechselForm, plan_id: v })}>
              <SelectTrigger data-testid="wechsel-plan"><SelectValue placeholder="Neuer Plan" /></SelectTrigger>
              <SelectContent>
                {plans.filter((p) => p.id !== wechselFuer?.plan_id).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} · {p.duration_label || (p.duration_months ? `${p.duration_months} Monate` : 'flexibel')} · {(p.monthly_price || p.price || 0).toFixed(2).replace('.', ',')} €/Monat</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-muted-foreground">Ab (Standard: heute)</label><Input type="date" value={wechselForm.ab} onChange={(e) => setWechselForm({ ...wechselForm, ab: e.target.value })} data-testid="wechsel-ab" /></div>
              <div><label className="text-xs text-muted-foreground">Abweichender Beitrag €</label><Input type="number" step="0.01" value={wechselForm.monatsbeitrag} onChange={(e) => setWechselForm({ ...wechselForm, monatsbeitrag: e.target.value })} placeholder="Planpreis" /></div>
            </div>
            <Input value={wechselForm.bemerkung} onChange={(e) => setWechselForm({ ...wechselForm, bemerkung: e.target.value })} placeholder="Bemerkung" />
            <p className="text-xs text-muted-foreground">Die alte Mitgliedschaft endet zum Stichtag (nicht als Kündigung), Mandat/Einzugstag werden übernommen.</p>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setWechselFuer(null)}>Abbrechen</Button><Button onClick={wechselSpeichern} disabled={aktionBusy} data-testid="wechsel-speichern">Wechseln</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {tab === 'cancelled' && (
        <div className="space-y-4" data-testid="cancelled-section">
          {istInhaber && (
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <Button size="sm" variant="outline" disabled={aktionBusy} data-testid="kuendigungen-zusammenlegen" onClick={async () => {
                setAktionBusy(true);
                try {
                  const v = (await membershipsAPI.kuendigungsDuplikate()).data;
                  if (!v.anzahl_gruppen) { toast.info('Keine doppelten Kündigungen'); return; }
                  if (!window.confirm(`${v.anzahl_gruppen} Personen haben mehrere gekündigte Datensätze (${v.anzahl_datensaetze} Doppel). Je Person bleibt der jüngste, die älteren werden als ersetzt markiert. Fortfahren?`)) return;
                  const r = (await membershipsAPI.kuendigungenZusammenlegen()).data;
                  toast.success(`${r.gruppen} Personen bereinigt, ${r.ersetzt} Doppel entfernt`);
                  if (r.namensdoppler?.length) toast.warning(`Zu prüfen: ${r.namensdoppler.map(n => n.name).join(', ')} — gleicher Name, verschiedene Mitglieder`);
                  fetchData();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Zusammenlegen fehlgeschlagen'); }
                finally { setAktionBusy(false); }
              }}>Doppelte Kündigungen zusammenlegen</Button>
              <span>Alte Import-Datensätze (Lexware/Octiv) neben dem echten Vertrag werden je Person zu einer Kündigung.</span>
            </div>
          )}
          {/* Search */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="cancelled-search"
                value={cancelledSearch}
                onChange={e => setCancelledSearch(e.target.value)}
                placeholder="Gekündigte Mitglieder suchen..."
                className="pl-9"
              />
              {cancelledSearch && (
                <button onClick={() => setCancelledSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <SortWahl sort={sortGek} setSort={setSortGek} datum="gekuendigt" datumText="Kündigung" testid="cancelled-sort" />
          </div>
          {cancelledSearch && (
            <p className="text-xs text-muted-foreground">{filteredCancelled.length} von {cancelledMembers.length} Ergebnissen</p>
          )}

          <Card className="border-border/60" data-testid="cancelled-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60">
                      <SortKopf sort={sortGek} setSort={setSortGek} k="name" className="text-left">Name</SortKopf>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Kontakt</th>
                      <SortKopf sort={sortGek} setSort={setSortGek} k="plan" className="text-left">Plan</SortKopf>
                      <SortKopf sort={sortGek} setSort={setSortGek} k="monatlich" className="text-right">Monatlich</SortKopf>
                      <SortKopf sort={sortGek} setSort={setSortGek} k="beginn" className="text-left">Vertragsbeginn</SortKopf>
                      <SortKopf sort={sortGek} setSort={setSortGek} k="gekuendigt" className="text-left">Gekündigt am</SortKopf>
                      <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Nr.</th>
                      {istInhaber && <th className="p-3"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCancelled.map(c => (
                      <tr key={c.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`cancelled-row-${c.id}`}>
                        <td className="p-3 font-medium">{c.name || '—'}</td>
                        <td className="p-3">
                          {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                          {c.telefon && <div className="text-xs text-muted-foreground">{c.telefon}</div>}
                          {!c.email && !c.telefon && <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-[10px]">{c.plan} {c.duration ? `(${c.duration})` : ''}</Badge>
                        </td>
                        <td className="p-3 text-right font-semibold">{c.monthly_price ? `${c.monthly_price.toFixed(2).replace('.', ',')} €` : '—'}</td>
                        <td className="p-3 text-xs">{c.start_date ? new Date(c.start_date).toLocaleDateString('de-DE') : '—'}</td>
                        <td className="p-3 text-xs text-red-600">{c.cancelled_date ? new Date(c.cancelled_date).toLocaleDateString('de-DE') : '—'}</td>
                        <td className="p-3 font-mono text-xs">{c.vertragsnummer || '—'}</td>
                        {istInhaber && (
                          <td className="p-3 text-right">
                            {c.type === 'membership' && (
                              <button type="button" className="text-muted-foreground hover:text-red-600" title="Aus der Liste entfernen (bleibt im Audit)" data-testid={`entfernen-${c.id}`} onClick={async () => {
                                const grund = window.prompt(`„${c.name}" aus den Kündigungen entfernen — Grund?`, 'Testdaten / Doppel');
                                if (grund === null) return;
                                try { await membershipsAPI.entfernen(c.id, grund); toast.success('Entfernt'); fetchData(); }
                                catch (e) { toast.error(e?.response?.data?.detail || 'Entfernen fehlgeschlagen'); }
                              }}><X className="w-4 h-4" /></button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {filteredCancelled.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                        {cancelledSearch ? (
                          <div className="space-y-1">
                            <Search className="w-8 h-8 mx-auto opacity-30" />
                            <p>Kein Ergebnis für „{cancelledSearch}"</p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <Check className="w-8 h-8 mx-auto opacity-30 text-emerald-500" />
                            <p>Keine gekündigten Mitgliedschaften</p>
                          </div>
                        )}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TRANSACTIONS TAB */}
      {tab === 'transactions' && istInhaber && (
        <Card className="border-border/60">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Datum</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Mitglied</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Plan</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Betrag</th>
                    <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id} className="border-b border-border/30 hover:bg-muted/30">
                      <td className="p-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString('de-DE')}</td>
                      <td className="p-3 font-medium">{t.member_name}</td>
                      <td className="p-3">{t.plan_name}</td>
                      <td className="p-3">{t.amount}&#8364;</td>
                      <td className="p-3">
                        <Badge variant="outline" className={`text-[10px] ${t.payment_status === 'paid' ? 'border-[#10B981] text-[#10B981]' : 'border-amber-500 text-amber-500'}`}>
                          {t.payment_status === 'paid' ? 'Bezahlt' : t.payment_status === 'pending' ? 'Ausstehend' : t.payment_status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Noch keine Transaktionen</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => { setAssignDialog(null); setMemberSearch(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{assignDialog?.name} - {assignDialog?.duration_label} zuweisen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Mitglied</label>
              <Input
                placeholder="Mitglied suchen..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="mb-2"
                data-testid="member-search-input"
              />
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {filteredMembers.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedMember(m.id); setMemberSearch(m.name); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors border-b last:border-0 ${selectedMember === m.id ? 'bg-primary/10 font-medium' : ''}`}
                      data-testid={`member-option-${m.id}`}
                    >
                      <span className="font-medium">{m.name}</span>
                      {m.email && <span className="text-muted-foreground ml-2 text-xs">{m.email}</span>}
                    </button>
                  ))}
                {members.filter(m => {
                  if (!memberSearch) return true;
                  const q = memberSearch.toLowerCase();
                  return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-3">Kein Mitglied gefunden</p>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Zahlungsmethode</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="select-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manuell (Bar/Überweisung)</SelectItem>
                  <SelectItem value="stripe">Online-Zahlung (Stripe)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="starter-kit-checkbox">
              <input type="checkbox" checked={hasStarterKit} onChange={(e) => setHasStarterKit(e.target.checked)} className="rounded border-input" />
              Starter-Kit (Boxhandschuhe + Schienbeinschoner, 30 Min betreuter Einstieg, 3 Monate P1 Akademie)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Monatsbeitrag abweichend (€)</label>
                <Input value={eigenerBeitrag} onChange={(e) => setEigenerBeitrag(e.target.value)} placeholder={`leer = ${assignDialog?.monthly_price?.toFixed(2).replace('.', ',')} €`} inputMode="decimal" data-testid="eigener-beitrag" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Bemerkung</label>
                <Input value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="z. B. Altpreis seit 2023" maxLength={200} data-testid="beitrag-bemerkung" />
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Plan:</span>
                <span className="font-medium">{assignDialog?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Laufzeit:</span>
                <span className="font-medium">{assignDialog?.duration_label}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Wochenbeitrag:</span>
                <span className="font-medium">{assignDialog?.weekly_price?.toFixed(2).replace('.', ',')} &#8364;</span>
              </div>
              <div className="flex justify-between text-sm border-t border-border pt-1 mt-1">
                <span className="text-muted-foreground font-semibold">Monatsbeitrag:</span>
                <span className="font-bold text-primary">{(eigenerBeitrag.trim() !== '' && !Number.isNaN(Number(eigenerBeitrag.replace(',', '.'))) ? Number(eigenerBeitrag.replace(',', '.')) : (assignDialog?.monthly_price ?? 0)).toFixed(2).replace('.', ',')} &#8364;{eigenerBeitrag.trim() !== '' && <span className="ml-1 text-[10px] font-normal text-muted-foreground">(abweichend)</span>}</span>
              </div>
            </div>
            <Button className="w-full" style={{ backgroundColor: assignDialog?.color }}
              onClick={handleAssign} disabled={!selectedMember || paying} data-testid="confirm-assign-btn">
              {paying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird verarbeitet...</> :
                paymentMethod === 'stripe' ? <><CreditCard className="w-4 h-4 mr-2" /> Zur Zahlung</> : 'Mitgliedschaft zuweisen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
